import { useEffect, useRef, useState } from 'react'
import { Printer, X } from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'

/**
 * Modal que VISUALIZA um PDF (blob URL) com botões Imprimir / Fechar.
 * Diferente do imprimirPdfBlob (que imprime uma vez e some), aqui o operador
 * vê o documento e pode imprimir QUANTAS vezes quiser antes de fechar.
 */
export default function PdfPreviewModal({
  url, titulo = 'Visualizar PDF', onClose,
}: { url: string; titulo?: string; onClose: () => void }) {
  const iframeRef = useRef<HTMLIFrameElement>(null)

  // ESC fecha o modal. Captura + stopPropagation pra NÃO disparar o ESC
  // hierárquico do PDV/Layout (que tentaria voltar/sair).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        e.preventDefault()
        onClose()
      }
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [onClose])

  const imprimir = () => {
    try {
      iframeRef.current?.contentWindow?.focus()
      iframeRef.current?.contentWindow?.print()
    } catch {
      window.open(url, '_blank')
    }
  }

  return (
    <div className="fixed inset-0 z-[120] bg-ink-950/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl h-[90vh] flex flex-col overflow-hidden"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-ink-100">
          <h3 className="font-semibold text-ink-800">{titulo}</h3>
          <button onClick={onClose} title="Fechar (Esc)" className="p-1.5 rounded hover:bg-ink-100 text-ink-500">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 bg-ink-100">
          <iframe ref={iframeRef} src={url} className="w-full h-full" title={titulo} />
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-ink-100">
          <button onClick={onClose} className="btn-secondary">Fechar</button>
          <button onClick={imprimir}
                  className="px-4 py-2 rounded bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium flex items-center gap-1.5">
            <Printer size={16} /> Imprimir
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * Hook pra abrir um PDF de um endpoint no modal de preview.
 * Uso:
 *   const { abrir, modal } = usePdfPreview()
 *   <button onClick={() => abrir('/caixa/sessoes/1/relatorio.pdf', 'Fechamento de caixa')}>PDF</button>
 *   {modal}
 */
export function usePdfPreview() {
  const [estado, setEstado] = useState<{ url: string; titulo?: string } | null>(null)
  const abrir = async (apiUrl: string, titulo?: string) => {
    try {
      const r = await api.get(apiUrl, { responseType: 'blob' })
      setEstado({ url: URL.createObjectURL(r.data), titulo })
    } catch {
      toast.error('Erro ao gerar PDF')
    }
  }
  const fechar = () => {
    setEstado(prev => { if (prev) URL.revokeObjectURL(prev.url); return null })
  }
  const modal = estado ? <PdfPreviewModal url={estado.url} titulo={estado.titulo} onClose={fechar} /> : null
  return { abrir, modal }
}
